// 📁 GameLobby.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import socket from "../socket";
import { useNavigate } from "react-router-dom";

export default function GameLobby() {
  const [status, setStatus] = useState(null);
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [myHunter, setMyHunter] = useState(null);
  const [endBanner, setEndBanner] = useState(null);
  const lastGame = JSON.parse(localStorage.getItem("lastGameResult") || "null");
  const [postGameLock, setPostGameLock] = useState(false);

  console.log("🔥 Lobby mounted, socket ID:", socket.id);

  // ======================================================
  // 🚀 STEP 1 — Identify IMMEDIATELY when socket connects
  // This fixes all real-time issues + target assignment + GPS
  // ======================================================
useEffect(() => {
  const handleConnect = async () => {
    console.log("⚡ Socket connected, sending identify...");
    try {
      const me = await axios.get("/api/me", { withCredentials: true });
      const uid = me.data.user._id;
      setUserId(uid);

      setStats({
        wins: me.data.user.wins ?? 0,
        losses: me.data.user.losses ?? 0,
      });

      socket.emit("identify", uid);
    } catch (err) {
      console.error("❌ Identification error:", err);
    }
  };

  socket.on("connect", handleConnect);

  // ✅ If already connected, run immediately
  if (socket.connected) handleConnect();

  return () => socket.off("connect", handleConnect);
}, []);


//Force logout
//useEffect(() => {
  //const handler = ({ reason }) => {
    //alert(`🚫 Logged out: ${reason}`);
    //window.location.href = "/"; // or /login
  //};

  //socket.on("forceLogout", handler);
  //return () => socket.off("forceLogout", handler);
//}, []);



  // ======================================================
  // 🚀 STEP 2 — Fetch approval status + join game room if approved
  // ======================================================

useEffect(() => {
  let cancelled = false;

  const init = async () => {
    try {
      // ✅ Determine post-game lock (don’t early-return)
      const raw = localStorage.getItem("lastGameResult");
      let locked = false;

      if (raw) {
        try {
          const last = JSON.parse(raw);
          const LOCK_MS = 12_000; // match your winner toast duration-ish
          locked = Date.now() - last.ts < LOCK_MS;
        } catch {}
      }

      if (!cancelled) setPostGameLock(locked);
      if (locked) console.log("🏁 Post-game lobby lock active");

      // ✅ ALWAYS fetch status so we don’t get stuck on “Checking status…”
      const res = await axios.get("/api/my-approval-status", { withCredentials: true });
      const data = res.data;

      if (cancelled) return;
      setStatus(data);

      // ☠️ Dead players stay in lobby
      if (data.isAlive === false) return;

      // Join room if approved
      if (data.approved && data.gameId) {
        socket.emit("joinGameRoom", data.gameId);
      }

      // ✅ Only redirect if NOT locked
      if (!locked && data.approved && data.gameStarted) {
        console.log("🚀 Redirecting to map");
        navigate("/map");
      }

      // ✅ If locked, schedule a re-check once lock expires
      if (locked) {
        setTimeout(() => {
          if (!cancelled) init(); // re-run once lock likely expired
        }, 1500);
      }
    } catch (err) {
      console.error("❌ Lobby init error:", err);
    }
  };

  init();
  return () => {
    cancelled = true;
  };
}, [navigate]);




  // ======================================================
  // 🚀 STEP 3 — Real-time approval event
  // ======================================================
  useEffect(() => {
    const handleApproved = (payload) => {
      console.log("🔥 Real-time APPROVED:", payload);

      setStatus({
        approved: true,
        gameId: payload.gameId,
        gameName: payload.gameName,
        gameStarted: false,
        isAlive: true,
      });

      // Identify again to guarantee server association
      socket.emit("identify", payload.userId);

      // Join game room instantly
      socket.emit("joinGameRoom", payload.gameId);
    };

    socket.on("playerApproved", handleApproved);
    return () => socket.off("playerApproved", handleApproved);
  }, []);
  
  useEffect(() => {
  const raw = localStorage.getItem("lastGameResult");
  if (!raw) return;

  try {
    const data = JSON.parse(raw);

    // only show if recent (10s)
    if (Date.now() - data.ts < 10_000) {
      setEndBanner(data);
      setTimeout(() => setEndBanner(null), 6000);
    }
  } catch (e) {}

  // ✅ clear so it doesn’t persist forever
  setTimeout(() => {
  localStorage.removeItem("lastGameResult");
}, 60_000); // 1 minute safety window

}, []);

  //hunter ID
  useEffect(() => {
  const handler = ({ hunterId, hunterName }) => {
    setMyHunter({ hunterId, hunterName });
  };

  socket.on("hunterAssigned", handler);
  return () => socket.off("hunterAssigned", handler);
}, []);


  // ======================================================
  // 🚀 STEP 4 — Real-time game start event
  // ======================================================
useEffect(() => {
  const handleGameStarted = ({ gameId }) => {
    const lastGame = localStorage.getItem("lastGameResult");
    if (lastGame) {
      console.log("🛑 Ignoring gameStarted — post-game state");
      return;
    }

    console.log("📡 WS — Game started:", gameId);
    navigate("/map");
  };

  socket.on("gameStarted", handleGameStarted);
  return () => socket.off("gameStarted", handleGameStarted);
}, [navigate]);

  
  
  //Reconnect after wake
  useEffect(() => {
  if (!userId) return;

  const onConnect = () => {
    socket.emit("identify", userId);
    if (status?.gameId) socket.emit("joinGameRoom", status.gameId);
  };

  socket.on("connect", onConnect);
  return () => socket.off("connect", onConnect);
}, [userId, status?.gameId]);

  
  useEffect(() => {
  if (!userId) return;

  let stopped = false;

  const poll = async () => {
    try {
      const res = await axios.get("/api/my-approval-status", {
        withCredentials: true,
      });

      if (stopped) return;

      const data = res.data;
      setStatus((prev) => {
        // avoid re-render spam if nothing changed
        const prevStr = JSON.stringify(prev);
        const nextStr = JSON.stringify(data);
        return prevStr === nextStr ? prev : data;
      });

      // If dead, stay put (no redirect)
      if (data.isAlive === false) return;

      // If approved, join the room (idempotent is fine)
      if (data.approved && data.gameId) {
        socket.emit("joinGameRoom", data.gameId);
      }

      // Only redirect when NOT in post-game lock
      if (!postGameLock && data.approved && data.gameStarted) {
        navigate("/map");
      }
    } catch (e) {
      // don’t hard-fail lobby; just keep polling
    }
  };

  // run immediately + then repeat
  poll();
  const t = setInterval(poll, 3500);

  return () => {
    stopped = true;
    clearInterval(t);
  };
}, [userId, postGameLock, navigate]);


  // ======================================================
  // UI
  // ======================================================
  if (!status) return <div>Checking status…</div>;

  return (
    <div style={{ padding: "20px" }}>
      <h2 className="text-xl font-bold">🎯 Game Lobby</h2>
	  {stats && (
  <div style={{
    marginTop: "12px",
    padding: "10px",
    background: "#111",
    borderRadius: "8px",
    color: "#fff",
    width: "fit-content"
  }}>
    <strong>📊 Your Stats</strong>
    <div>✅ Wins: {stats.wins}</div>
    <div>☠️ Losses: {stats.losses}</div>
  </div>
)}
{endBanner && (
  <div style={{
    background: "rgba(0,0,0,0.8)",
    color: "white",
    padding: "10px 14px",
    borderRadius: "10px",
    marginBottom: "12px",
    border: "2px solid gold"
  }}>
    {endBanner.iWon
      ? "🏆 YOU WIN!"
      : `🏆 Game Over — Winner: ${endBanner.winnerName}`}
  </div>
)}


      {!status.approved && <p>⏳ Waiting for admin approval…</p>}
	  {myHunter ? (
  <div style={{ marginTop: "10px", color: "#f87171", fontWeight: "bold" }}>
    👀 You will be hunted by: {myHunter.hunterName}
  </div>
) : (
  <div style={{ marginTop: "10px", color: "#9ca3af" }}>
    ⏳ Waiting for hunter assignment…
  </div>
)}


      {status.approved && !status.gameStarted && (
        <p>✅ Approved! Waiting for game start…</p>
      )}

      {status.gameStarted && (
        <p className="text-green-700">🚀 Game started — entering map…</p>
      )}
	  {postGameLock && (
  <p style={{ marginTop: 10, color: "#facc15", fontWeight: "bold" }}>
    🏁 Game ended — returning to lobby… standby for the next game.
  </p>
)}


      {status.gameName && (
        <div style={{ marginTop: "10px" }}>
          <strong>Game:</strong> {status.gameName}
        </div>
      )}
    </div>
  );
}
